import { Mutex } from 'async-mutex'
import { createRoot, DEV } from 'solid-js'
import { StateT as GeneratedStateT, StateType as GeneratedStateType } from '../../../engine/bindings/typescript/tbs'
import { Group, Mesh, OrthographicCamera, Scene, WebGLRenderer } from "three"
import { POSTGRES_MAX_INT } from '../../../lib/util/numbers'
import { createOffscreenCanvas } from "../../canvas/create_canvas"
import { getCanvas2dContext } from "../../canvas/get_canvas_2d_context"
import { loseWebGL2Context } from "../../canvas/loseWebGL2Context"
import type { Engine } from '../../core/engine/Engine.type'
import type { EntityGraphNode } from '../../core/engine/EntityGraphNode/EntityGraphNode.type'
import { toGeneratedEntity, toGeneratedPlayer } from '../../core/engine/engineWasmCodec'
import { ELEVATION_SIZE } from '../../core/entity/EntityElevation/elevation_consts'
import applyEntityRotationForMediumRocketsToNextObject3DFrame from '../../core/entity/EntityHeading/applyEntityRotationForMediumRocketsToNextObject3DFrame'
import applyEntityRotationToNextObject3DFrame from '../../core/entity/EntityHeading/applyEntityRotationToNextObject3DFrame'
import { EntityHeadingDeg } from '../../core/entity/EntityHeading/consts'
import { EntityTypeId } from '../../core/entity/EntityTypeId.enum'
import { isEntityDeadwood, isEntityForest, isEntityJungle, isEntityMountain, isEntityRiver, isEntityShoal, isEntitySwamp } from '../../core/entity/entityTypeMetaList.generated'
import type { Entity } from "../../core/entity/index"
import isEntityWater from '../../core/entity/isEntityWater'
import coord from '../../core/tile_position_xy/coord'
import { PI } from '../../core/util/math/Math'
import assertClassInDev from '../../ldsh/assertClassInDev'
import { VERTICES_PER_TILE } from '../consts'
import createEntityThreeModel from '../EntityThreeModel/createEntityThreeModel'
import loadEntityThreeModelGLTF from '../EntityThreeModel/loadEntityThreeModelGLTF'
import updateEntityThreeModel from '../EntityThreeModel/updateEntityThreeModel'
import isEntityTerrainMesh1 from '../objects/TerrainMesh1V2/isEntityTerrainMesh1'
import createTerrainMesh1V3Effect from '../objects/TerrainMesh1V3/createTerrainMesh1V3Effect'
import createWaterPlaneMesh from '../objects/WaterPlane/createWaterPlaneMesh'
import { createAmbientLight } from '../sky/createAmbientLight'
import { createHemisphereLight } from '../sky/createHemisphereLight'
import { useSharedDirectionalLight } from '../sky/useSunDirectionalLight'
import { UNIT_SPRITE_NAME } from '../sprite/createUnitSprite'
import createStandardUnitTextureEffect from '../texture/StandardUnitTexture/createStandardUnitTextureEffect'
import callAllUserDataUpdatesRecursive from './callAllUserDataUpdates'
import disposeRecursive from './disposeRecursive'
import { setEngineThreeMainRenderer } from './getSetEngineThreeMainRenderer'
import setObjPosition from './setObjPosition'

const mutex = new Mutex()

const MULT = (VERTICES_PER_TILE * (32 / 10))

const needsTerrainMesh1 = [
  EntityTypeId.Forest,
  EntityTypeId.Deadwood,
  EntityTypeId.Jungle,
  EntityTypeId.Swamp,
  // EntityTypeId.Marsh, // TODO
  // EntityTypeId.Lake,
  EntityTypeId.River,
  EntityTypeId.Sea,
  EntityTypeId.Shoal,
]

export default async function makeCanvasIntoEntityIcon3D(
  engine: Engine,
  offscreenCanvasElem: OffscreenCanvas,
  entNode: EntityGraphNode) {
  if (DEV) {
    // console.log('makeCanvasIntoEntityIcon3D.entNode', entNode)
    if (typeof entNode !== 'function') {
      throw new Error('makeCanvasIntoEntityIcon3D: entNode must be an EntityGraphNode')
    }
  }

  // console.log('makeCanvasIntoEntityIcon3D.init')
  return new Promise<void>((resolve) => {
    const { width: canvasWidth, height: canvasHeight } = offscreenCanvasElem
    const ctx = getCanvas2dContext(offscreenCanvasElem)

    if (ctx) {
      const entityTypeId = entNode.etype_id
      mutex.runExclusive(async () => {
        // console.log('makeCanvasIntoEntityIcon3D.runExclusive')
        let offscreenCanvas: OffscreenCanvas | null = null
        let renderer: WebGLRenderer | null = null
        let scene: Scene | null = null
        let camera: OrthographicCamera | null = null
        try {
          offscreenCanvas = createOffscreenCanvas(canvasWidth, canvasHeight)
          // const ent = {
          //   ...untrack(entNode),
          //   x: 0,
          //   y: 0,
          // }

          // engine.state.ents = [ent]

          renderer = new WebGLRenderer({
            canvas: offscreenCanvas,
            alpha: true,
            antialias: true,
          })
          // renderer = createMainRenderer(offscreenCanvas)
          renderer.setClearColor(0x000000, 0)

          setEngineThreeMainRenderer(engine, renderer)

          // Utility scene and camera
          scene = new Scene()
          // set pink background
          // scene.background = new Color(0xffaaaa)

          // Define orthographic camera parameters
          // Controls how much of the scene is visible
          const frustumSize = getFrustumSize(entityTypeId)
          const aspect = canvasWidth / canvasHeight
          const left = -frustumSize * aspect / 2
          const right = frustumSize * aspect / 2
          const top = frustumSize / 2
          const bottom = -frustumSize / 2
          const near = 0.1
          const far = 1000

          camera = new OrthographicCamera(left, right, top, bottom, near, far)
          // camera.lookAt(0, 0, 0) // Ensure it looks at the scene center

          scene.add(createAmbientLight())
          scene.add(createHemisphereLight())
          scene.add(useSharedDirectionalLight(engine))

          if (needsTerrainMesh1.includes(entNode.etype_id)) {
            createRoot((dispose) => {
              const ent = {
                ...entNode.raw(),
                x: 0,
                y: 0,
              }
              // console.log('entNode.raw().cloned()', entityDisplayName(ent), deepClone(ent))
              // modifyMutable(engine, produce((draft) => {
              //   draft.state.ents = [ent]
              // }))
              const bootstrapState = new GeneratedStateT(
                1,
                null,
                null,
                null,
                GeneratedStateType.Map,
                1,
                1,
                [toGeneratedEntity(ent)],
                engine.state.players.map(toGeneratedPlayer)
              )
              void engine.bootstrapWasmState(bootstrapState)

              createTerrainMesh1V3Effect(engine, scene!)
              engine.gcELL().push(dispose)
            })
          }

          const loadingModelPromise = loadEntityThreeModelGLTF(engine, entNode)
          const obj = await loadingModelPromise
          const entityThreeModel = createEntityThreeModel(entNode)
          entityThreeModel.obj = obj
          // console.log('ent', ent)

          // need createRoot because something is using a solid-js cached obj
          // TODO can this be untrack instead of createRoot?
          createRoot((disposeRoot) => {
            updateEntityThreeModel(entityThreeModel, entNode, engine)
            disposeRoot()
          })

          setObjPosition(obj, coord(0, 0))
          obj.userData.heading = obj.userData.targetHeading = EntityHeadingDeg.North
          // addProcedurallyGeneratedEntityTextures(engine, obj, entNode)
          createStandardUnitTextureEffect(engine, obj, entNode)
          applyEntityRotationToNextObject3DFrame(obj, POSTGRES_MAX_INT)
          scene.add(obj)

          const HPBar = obj.getObjectByName(UNIT_SPRITE_NAME)
          if (HPBar) {
            HPBar.visible = false
          }

          if (isEntityRiver(entNode) || isEntityShoal(entNode)) {
            // scene.position.z += MULT
            scene.position.z += MULT * 4
            // scene.position.x += MULT
            // scene.position.y -= MULT
          }
          if (isEntityWater(entNode) || isEntityShoal(entNode)) {
            obj.position.y -= 4
            const waterPlane = createWaterPlaneMesh(engine, engine.state.width, engine.state.height)
            if (isEntityRiver(entNode) || isEntityShoal(entNode)) {
              waterPlane.position.z += MULT
              waterPlane.position.x += MULT
              waterPlane.position.y -= 5
            }
            scene.add(waterPlane)
          }

          if (isEntityForest(entNode) || isEntityJungle(entNode) || isEntitySwamp(entNode) || isEntityDeadwood(entNode)) {
            camera.position.set(0, MULT, MULT)
            // scene.rotateX(0.5 + ((PI * 3) / 4))
            scene.rotateX(-PI / 16)
            scene.rotateY(((PI) / 4))
            camera.lookAt(scene.position)
          } else if (isEntityMountain(entNode)) {
            camera.position.set(0, MULT, -MULT) // Position the camera above
            // obj.rotateY((PI * 3) / 4)
            scene.rotateX((PI * 1) / 12)
            camera.lookAt(scene.position)
          } else if (isEntityRiver(entNode) || isEntityShoal(entNode)) {
            // scene.position.z += MULT * (1/2)
            // scene.position.z += MULT
            scene.position.z += MULT * (12 / 10)
            scene.position.y += 7 * MULT

            camera.position.set(0, MULT, MULT)
            // scene.rotateX(PI + ((0.5 + ((PI * 3) / 4))))
            scene.rotateX(((7 * PI) + 2) / 4)
            // scene.rotateZ(-((PI) / 4))
            scene.rotateY(-((PI) / 4))
            camera.lookAt(scene.position)
            camera.position.y += (MULT) / 2
            // camera.position.y -= 10
          } else if (isEntityTerrainMesh1(entNode)) {
            obj.position.y = ELEVATION_SIZE * 4
            camera.position.set(0, MULT, MULT)
            // scene.rotateX(PI + ((0.5 + ((PI * 3) / 4))))
            scene.rotateX(((7 * PI) + 2) / 4)
            // scene.rotateZ(-((PI) / 4))
            scene.rotateY(-((PI) / 4))
            camera.lookAt(scene.position)
          } else if (entityTypeId === EntityTypeId.Infantry) {
            // obj.userData.update(1,1)
            obj.rotateY((-PI * 1) / 8)
            obj.rotateX((PI * 1) / 32)
            obj.rotateZ((-PI * 1) / 32)
            // camera.lookAt(obj.position)
            // // obj.rotateY((PI * 3) / 4)
            // obj.rotateX((TWO_PI) / 4)
            // // obj.rotateY((PI * 1) / 8)
            // // obj.rotateZ((PI * 1) / 4)
            camera.position.y += 10
            // camera.position.y += 20
            camera.position.z += 4
            // scene.userData.update(1,1)
            // scene.userData.update(1,1)
            callAllUserDataUpdatesRecursive(obj, 1, POSTGRES_MAX_INT)
          } else if (entityTypeId === EntityTypeId.MediumArtillary) {
            const barrelBaseMesh = scene.getObjectByName('BarrelBase') as Mesh
            assertClassInDev(barrelBaseMesh, Mesh)
            barrelBaseMesh.rotateX(-PI / 16)

            camera.position.set(0, MULT, -MULT) // Position the camera above
            camera.position.y -= 16
            obj.rotateY((PI * 3) / 4)
            camera.lookAt(obj.position)
            camera.position.y += 6
          } else if (entityTypeId === EntityTypeId.LightTank) {

            const barrelMesh = scene.getObjectByName('Barrel') as Mesh
            assertClassInDev(barrelMesh, Mesh)
            barrelMesh.rotateX(-PI / 32)

            camera.position.set(0, MULT, -MULT) // Position the camera above
            camera.position.y -= 16
            obj.rotateY((PI * 3) / 4)
            camera.lookAt(obj.position)
            camera.position.y += 4.5
            camera.position.x -= 0.25
          } else if (entityTypeId === EntityTypeId.MediumGunAntiAir) {

            const barrelMesh = scene.getObjectByName('Barrel') as Mesh
            assertClassInDev(barrelMesh, Mesh)
            barrelMesh.rotateX(-PI / 16)

            camera.position.set(0, MULT, -MULT) // Position the camera above
            camera.position.y -= 16
            obj.rotateY((PI * 2.9) / 4)
            camera.lookAt(obj.position)
            camera.position.y += 7
          } else if (entityTypeId === EntityTypeId.MediumRockets) {
            const turretMesh = scene.getObjectByName('Turret') as Mesh
            assertClassInDev(turretMesh, Mesh)

            const rocketPodMesh = turretMesh.getObjectByName('RocketPod') as Mesh
            assertClassInDev(rocketPodMesh, Mesh)

            turretMesh.userData.targetHeading = EntityHeadingDeg.South
            rocketPodMesh.userData.raise = true
            applyEntityRotationForMediumRocketsToNextObject3DFrame(turretMesh, rocketPodMesh, POSTGRES_MAX_INT)
            turretMesh.userData.targetHeading = 0
            applyEntityRotationForMediumRocketsToNextObject3DFrame(turretMesh, rocketPodMesh, POSTGRES_MAX_INT)
            camera.position.set(0, MULT, -MULT) // Position the camera above
            camera.position.y -= 12
            obj.rotateY((PI * 3) / 4)
            camera.lookAt(obj.position)
            camera.position.y += 5
          } else if (entityTypeId === EntityTypeId.MediumTank) {

            const turretGroup = scene.getObjectByName('Turret') as Group
            assertClassInDev(turretGroup, Group)
            turretGroup.rotateZ(PI * (2 / 8))

            obj.rotateX(PI * -(1 / 8))

            camera.position.set(0, MULT, -MULT) // Position the camera above
            camera.position.y -= 16
            obj.rotateY(PI * (7 / 8))
            camera.lookAt(obj.position)
            camera.position.y += 4.5
            camera.position.x -= 0.25
          } else {
            //   camera.position.set(0, MULT, -MULT) // Position the camera above
            //   camera.position.set(0, -10 + MULT, -MULT - 10) // Position the camera above
            // } else {
            camera.position.set(0, MULT, -MULT) // Position the camera above
            // }
            scene.rotateY((PI * 3) / 4)
            camera.lookAt(scene.position)
            if (entityTypeId === EntityTypeId.LightRecon) {
              camera.position.y += 3
            } else if (entityTypeId === EntityTypeId.APC) {
              camera.position.y += 3.5
            } else if (entityTypeId === EntityTypeId.TransportCopter) {
              camera.position.y += 8.5
              // } else if (entityTypeId === EntityTypeId.MediumArtillary) {
              //   camera.position.y += 5
              // } else if (entityTypeId === EntityTypeId.LightTank) {
              //   camera.position.y += 3
              // } else if (entityTypeId === EntityTypeId.MediumGunAntiAir) {
              //   camera.position.y += 6
            } else if (entityTypeId === EntityTypeId.MediumCopter) {
              camera.position.y += 4.5
            } else if (entityTypeId === EntityTypeId.MediumMissileAntiAir) {
              camera.position.y += 5
              // } else if (entityTypeId === EntityTypeId.MediumRockets) {
              //   camera.position.y += 3.5
              // } else if (entityTypeId === EntityTypeId.MediumTank) {
              //   camera.position.y += 4
            }
          }

          renderer.render(scene, camera)

          ctx.clearRect(0, 0, canvasWidth, canvasHeight)
          ctx.drawImage(offscreenCanvas, 0, 0)
          // console.log('makeCanvasIntoEntityIcon3D.runExclusive.EOF')
        } finally {
          // console.log('makeCanvasIntoEntityIcon3D.runExclusive.finally')
          if (renderer) {
            renderer.forceContextLoss()
          }
          // console.log(scene)
          disposeRecursive(renderer!)
          disposeRecursive(scene!)
          disposeRecursive(camera!)
          loseWebGL2Context(offscreenCanvas!)
          resolve()
        }
      })
    }
  })
}

function getFrustumSize(entityTypeId: EntityTypeId): number {
  if (entityTypeId === EntityTypeId.Shoal) {
    return MULT * 1
  }
  if (entityTypeId === EntityTypeId.River) {
    return MULT * 1
  }
  if (entityTypeId === EntityTypeId.Infantry) {
    return MULT * 0.3
  }
  if (entityTypeId === EntityTypeId.LightRecon) {
    return MULT * 0.475
  }
  if (entityTypeId === EntityTypeId.APC) {
    return MULT * 0.5
  }
  if (entityTypeId === EntityTypeId.TransportCopter) {
    return MULT * 0.8
  }
  if (entityTypeId === EntityTypeId.MediumArtillary) {
    return MULT * 0.65
  }
  if (entityTypeId === EntityTypeId.LightTank) {
    return MULT * 0.7
  }
  if (entityTypeId === EntityTypeId.MediumCopter) {
    return MULT * 0.8
  }
  if (entityTypeId === EntityTypeId.MediumGunAntiAir) {
    return MULT * 0.9
  }
  if (entityTypeId === EntityTypeId.MediumMissileAntiAir) {
    return MULT * 0.65
  }
  if (entityTypeId === EntityTypeId.MediumRockets) {
    return MULT * 0.6
  }
  if (entityTypeId === EntityTypeId.MediumTank) {
    return MULT * 0.7
  }
  if (isEntityTerrainMesh1({ etype_id: entityTypeId } as Entity)) {
    return MULT * 0.44
  }
  return MULT
}
